// CommentBody — the ONE shared renderer for a comment's text (roadmap #6, rich comments). Used by both the
// guest book (events-web) and the host/admin drawer (apps/app) so rendering is identical everywhere.
//
// Renders the text as MARKDOWN (bold/italic/lists/code/quotes; bare URLs + `[text](url)` become clickable
// links) via `marked`, then runs it through the STRICT comment sanitizer (`sanitizeCommentHtml`) — raw HTML,
// scripts, images, iframes, and styles are stripped, so a comment can never inject anything. Links open in a
// new tab as `nofollow noopener noreferrer ugc`. Video links to an allowlist (YouTube/Vimeo/TED) additionally
// render a click-to-play facade BELOW the text (see VideoEmbed) — built from the parsed id, never raw embed HTML.
// Game/utility command results (e.g. "🎲 Roll 2d6: …") are already plain markdown (resolved server-side), so
// they render here with no special handling.
import { useMemo } from 'react';
import { Marked } from 'marked';

import { sanitizeCommentHtml } from './sanitize';
import { VideoEmbed, findVideoEmbeds } from './VideoEmbed';

export interface CommentBodyProps {
  text?: string | null;
}

// Escape the characters that can start an HTML element (or attribute) so a raw-HTML token renders as the
// literal characters a guest typed, never as markup.
const escapeRawHtml = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

// A comment is MARKDOWN or plain text ONLY — never raw HTML. By default `marked` passes inline/block HTML
// straight through, and our comment sanitizer then RENDERS it (it allows <b>/<a>/<h3>/… as legitimate
// markdown output), so a guest who types `<b>hi</b>` or `<h1>` sees it formatted — which is not allowed.
// We neutralize raw HTML AT THE SOURCE with a renderer whose `html` hook (invoked for every block AND inline
// HTML token) escapes the token to literal text. Markdown itself (emphasis, lists, blockquotes, code spans,
// links, bare-URL autolinks) is untouched — those are `strong`/`code`/`link`/… tokens, not `html` — so `<`
// inside a code span still displays correctly. `sanitizeCommentHtml` remains a second layer over whatever
// safe markup marked legitimately generates.
const commentMarked = new Marked({ gfm: true, breaks: true });
commentMarked.use({
  renderer: {
    html: (token: unknown): string => escapeRawHtml(typeof token === 'string' ? token : ((token as { text?: string })?.text ?? '')),
  },
});

// Inject the small markdown stylesheet ONCE (id-guarded), so comment paragraphs/lists/code read tightly inside
// the card instead of with the browser's default block margins. SSR-safe (no document → skip).
const STYLE_ID = 'em-comment-body-style';
const ensureStyle = (): void => {
  if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) {
    return;
  }
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent =
    '.em-comment-md>:first-child{margin-top:0}.em-comment-md>:last-child{margin-bottom:0}' +
    '.em-comment-md p{margin:0 0 .4em}.em-comment-md ul,.em-comment-md ol{margin:.2em 0 .4em 1.2em;padding:0}' +
    '.em-comment-md pre{overflow:auto;padding:.5em .6em;background:var(--mantine-color-default-hover,rgba(0,0,0,.05));border-radius:6px}' +
    '.em-comment-md code{font-size:.9em}.em-comment-md a{text-decoration:underline}' +
    '.em-comment-md blockquote{margin:.2em 0;padding-left:.6em;border-left:3px solid var(--mantine-color-default-border,rgba(0,0,0,.15));color:var(--mantine-color-dimmed,#868e96)}';
  document.head.appendChild(el);
};

export function CommentBody({ text }: CommentBodyProps) {
  const md = (text ?? '').toString();

  ensureStyle();

  const html = useMemo(
    () => sanitizeCommentHtml(commentMarked.parse(md, { async: false }) as string),
    [md],
  );
  const videos = useMemo(() => findVideoEmbeds(md), [md]);

  return (
    <div className="em-comment-body" style={{ fontSize: 'var(--mantine-font-size-md, 16px)', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
      <div className="em-comment-md" dangerouslySetInnerHTML={{ __html: html }} />
      {videos.map((v) => (
        <VideoEmbed key={v.key} video={v} />
      ))}
    </div>
  );
}
